import React from 'react';
import {
  AppBar,
  Avatar,
  Divider,
  Drawer,
  Hidden,
  Link,
  List,
  Toolbar,
  Typography,
  makeStyles
} from '@material-ui/core';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';

import NavItem from 'src/components/NavItem';
import { getUser } from 'src/utils/Common';
import { getMenu } from 'src/utils/storage/NavBarAdet';
import PersonalCard from '../../views/Profile/PersonalCard';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  mobileDrawer: { width: 256 },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)'
  },
  navigation: {
    overflow: 'auto',
    padding: theme.spacing(0, 2, 2, 2),
    flexGrow: 1
  },
  profile: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center'
  },
  avatar: {
    cursor: 'pointer',
    width: 40,
    height: 40
  },
  details: {
    marginLeft: theme.spacing(2)
  },
  divider: {
    margin: theme.spacing(1, 0)
  }
}));

// Yardımcı Fonksiyonlar (Bileşen dışında tanımlanması performansı artırır)
const renderNavItems = ({ items, pathname, setOpenUserCard, depth = 0, isTechnician }) => (
  <List disablePadding>
    {items.reduce((acc, item, index) => {
      // Klima teknisyeni kontrolü
      if (isTechnician && item.klima_teknisyeni_gizle) {
        return acc;
      }

      const key = `${item.title}-${item.href || ''}-${depth}-${index}`;
      
      if (item.items) {
        const open = matchPath(pathname, {
          path: item.href,
          exact: false
        });

        acc.push(
          <NavItem
            depth={depth}
            icon={item.icon}
            key={key}
            label={item.label}
            open={Boolean(open)}
            title={item.title}
          >
            {renderNavItems({
              items: item.items,
              pathname,
              setOpenUserCard,
              depth: depth + 1,
              isTechnician
            })}
          </NavItem>
        );
      } else {
        acc.push(
          <NavItem
            depth={depth}
            href={item.href}
            icon={item.icon}
            key={key}
            label={item.label}
            title={item.title}
            onClick={() => setOpenUserCard(item.title === 'Dijital Personel Kartı')}
          />
        );
      }
      return acc;
    }, [])}
  </List>
);

function NavBar({ openMobile, onMobileClose, className, ...rest }) {
  const classes = useStyles();
  const location = useLocation();
  const [openUserCard, setOpenUserCard] = useState(false);
  
  // Verileri çekme
  const menuItems = getMenu();
  const sessionUser = getUser();
  const technicianImage = localStorage.getItem('UserPhoto');

  // Teknisyenlik durumunu hesapla (Memoize edilerek performans korunur)
  const isKlimaTeknisyeni = useMemo(() => {
    if (!sessionUser?.TechnicianType) return false;
    return sessionUser.TechnicianType.split(',').some(type => parseInt(type) === 26);
  }, [sessionUser]);

  const userModel = useMemo(() => ({
    name: `${sessionUser?.FirstName || ''} ${sessionUser?.LastName || ''}`,
    bio: `${sessionUser?.ServiceShopCode || ''} ${sessionUser?.ServiceShopName || ''}`,
    avatar: technicianImage,
    technicianCode: sessionUser?.TechnicianCode
  }), [sessionUser, technicianImage]);

  // Sayfa değiştiğinde mobil menüyü kapat
  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]); // eslint-disable-line

  const content = (
    <div className={clsx(classes.root, className)} {...rest}>
      <AppBar color="secondary" position="static" elevation={0}>
        <Toolbar>
           {/* İhtiyaç halinde logo/versiyon buraya eklenebilir */}
        </Toolbar>
      </AppBar>

      <nav className={classes.navigation}>
        {renderNavItems({
          items: menuItems,
          pathname: location.pathname,
          setOpenUserCard,
          isTechnician: isKlimaTeknisyeni
        })}
      </nav>

      <Divider className={classes.divider} />

      <div className={classes.profile}>
        <Avatar
          alt="User Avatar"
          className={classes.avatar}
          src={technicianImage}
          component={RouterLink}
          to="/profile/1/timeline"
        />
        <div className={classes.details}>
          <Link
            component={RouterLink}
            to="/profile/1/timeline"
            variant="h5"
            color="textPrimary"
            underline="none"
          >
            {userModel.name}
          </Link>
          <Typography variant="body2" color="textSecondary">
            {sessionUser?.ServiceShopName}
          </Typography>
        </div>
      </div>

      <PersonalCard
        openUserCard={openUserCard}
        setOpenUserCard={setOpenUserCard}
        user={userModel}
      />
    </div>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
}

NavBar.propTypes = {
  className: PropTypes.string,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;